/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * "Is this your agency's OLX account?" -- shown right after coming back from
 * OLX's sign-in, because OLX skips its own screen when the browser is already
 * signed in and connects whichever account was open. Same overlay pattern as
 * the portal setup modal.
 *
 * OLX has no integrator terms for the agency to accept, so the same pop-up
 * summarises Housien's OLX publishing terms and "Sim, usar esta conta" stays
 * disabled until the box is ticked; confirming sends the accepted version.
 */

import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Label, List, ListItem, Text, UnstyledButton } from '@/shared/ui';
import { CheckboxRaw } from '@/shared/ui/components/form/inputs/Checkbox';
import Link from '@/shared/ui/components/basic/Link';
import { OLX_PURPLE, OlxBadge } from './OlxConnectionBlock';
import { OLX_TERMS_URL } from './constants';
import type { PortalSettings } from './constants';

/** i18n keys of the summary lines, in display order. */
const TERMS_POINTS = ['olx.termos1', 'olx.termos2', 'olx.termos3', 'olx.termos4', 'olx.termos5'];

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface OlxConfirmAccountModalProps {
  open: boolean;
  settings: PortalSettings;
  saving: boolean;
  /** "Sim, usar esta conta". */
  onConfirm: () => void;
  /** "Não, trocar de conta": disconnects and explains how to switch. */
  onSwitch: () => void;
  /** Decide later: the card keeps showing the pending state. */
  onClose: () => void;
  t: Translate;
}

/**
 * The confirmation pop-up.
 *
 * @param props - Visibility, the connected account and the two answers.
 * @returns The overlay, or null when closed.
 */
export function OlxConfirmAccountModal({
  open, settings, saving, onConfirm, onSwitch, onClose, t,
}: OlxConfirmAccountModalProps) {
  const [accepted, setAccepted] = useState(false);
  // Every opening asks again: a tick from an earlier opening never carries over.
  useEffect(() => { if (!open) setAccepted(false); }, [open]);
  if (!open) return null;
  const name = settings.olx_user_name || null;
  const email = settings.olx_user_email || null;
  const account = name || email;

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center" data-testid="olx-confirm-modal">
      <div className="absolute inset-0 bg-[var(--surface-inverse)]/40" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="olx-confirm-title"
        className="relative w-full max-w-md mx-(--space-4) max-h-[90vh] overflow-y-auto rounded-(--radius-lg) bg-(--surface-primary) border border-(--border-subtle) shadow-xl"
      >
        <div className="flex items-center justify-between px-(--space-6) py-(--space-4) border-b border-(--border-subtle)">
          <Text id="olx-confirm-title" className="m-0 text-base font-semibold text-(--text-primary)">
            {t('olx.confirmarTitulo')}
          </Text>
          <UnstyledButton
            onClick={onClose}
            aria-label={t('olx.decidirDepois')}
            data-testid="olx-confirm-close"
            className="w-7 h-7 flex items-center justify-center rounded-(--radius-md) border-none bg-transparent hover:bg-(--surface-secondary) cursor-pointer text-(--text-tertiary) text-lg"
          >
            &times;
          </UnstyledButton>
        </div>

        <div className="px-(--space-6) py-(--space-5) flex flex-col gap-(--space-4)">
          <div className="flex items-center gap-(--space-3) p-(--space-3) rounded-(--radius-lg) bg-(--surface-secondary) border border-(--border-subtle)">
            <OlxBadge size={40} />
            <div className="min-w-0">
              <Text className="m-0 text-sm font-semibold text-(--text-primary) truncate">
                {account || t('olx.contaConectadaSemNome')}
              </Text>
              {name && email && <Text className="m-0 text-xs text-(--text-tertiary) truncate">{email}</Text>}
            </div>
          </div>

          <Text className="m-0 text-sm text-(--text-secondary) leading-relaxed">
            {account ? t('olx.confirmarDescricao', { account }) : t('olx.confirmarDescricaoSemNome')}
          </Text>

          <OlxTermsConsent accepted={accepted} onChange={setAccepted} t={t} />

          <div className="flex flex-col gap-(--space-2-5)">
            <UnstyledButton
              onClick={onConfirm}
              disabled={saving || !accepted}
              data-testid="olx-confirm-account-btn"
              className="w-full py-(--space-3) text-sm font-semibold rounded-(--radius-md) text-white border-none cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: OLX_PURPLE }}
            >
              {t('olx.confirmarSim')}
            </UnstyledButton>
            <UnstyledButton
              onClick={onSwitch}
              disabled={saving}
              data-testid="olx-switch-account-btn"
              className="w-full py-(--space-3) text-sm font-semibold rounded-(--radius-md) border border-(--border-subtle) bg-[var(--surface-primary)] text-(--text-primary) cursor-pointer hover:bg-(--surface-secondary) disabled:opacity-60"
            >
              {t('olx.confirmarTrocar')}
            </UnstyledButton>
          </div>

          <Text className="m-0 text-center text-xs text-(--text-tertiary)">{t('olx.confirmarNota')}</Text>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Summary of the OLX publishing terms and the box that accepts them on the
 * agency's behalf; the full text is the OLX section of the Terms of Service.
 *
 * @param props - Whether the box is ticked, its change handler and ``t``.
 * @returns The terms block.
 */
function OlxTermsConsent({ accepted, onChange, t }: {
  accepted: boolean; onChange: (accepted: boolean) => void; t: Translate;
}) {
  return (
    <div className="flex flex-col gap-(--space-3)" data-testid="olx-terms">
      <div className="rounded-(--radius-lg) bg-(--surface-secondary) p-(--space-4)">
        <Text className="m-0 text-xs font-semibold text-(--text-primary) mb-(--space-2)">{t('olx.termosTitulo')}</Text>
        <List size="sm" spacing="sm" className="m-0 pl-0 list-none flex flex-col gap-(--space-1-5)">
          {TERMS_POINTS.map((key) => (
            <ListItem
              key={key}
              className="text-xs text-(--text-secondary) leading-relaxed"
              icon={<Text component="span" className="block w-1 h-1 mt-(--space-1-5) rounded-full bg-(--text-tertiary)" />}
            >
              {t(key)}
            </ListItem>
          ))}
        </List>
      </div>
      <Label className="flex items-start gap-(--space-2-5) cursor-pointer">
        <div className="shrink-0 pt-(--space-0-5)" data-testid="olx-terms-checkbox">
          <CheckboxRaw name="olx-terms-consent" value={accepted} onToggle={(v) => onChange(v)} />
        </div>
        <Text component="span" className="text-xs text-(--text-primary) leading-snug" onClick={() => onChange(!accepted)}>
          {t('olx.termosAceite')}{' '}
          <Link
            href={OLX_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e: MouseEvent) => e.stopPropagation()}
            className="text-xs font-semibold underline cursor-pointer"
            style={{ color: OLX_PURPLE }}
          >
            {t('olx.termosLink')}
          </Link>
          {' '}{t('olx.termosAceiteFim')}
        </Text>
      </Label>
    </div>
  );
}
